import React from 'react';
import { motion } from 'framer-motion';
import { SunriseIcon, SunIcon, MoonIcon, ClockIcon } from 'lucide-react';
export interface DayActivity {
  timeSlot: 'morning' | 'afternoon' | 'evening';
  title: string;
  description: string;
  duration: string;
  category: string;
}
export interface ItineraryDayProps {
  dayNumber: number;
  date: Date;
  activities: DayActivity[];
}
const slotConfig = {
  morning: {
    icon: SunriseIcon,
    label: 'Morning',
    accent: 'from-amber-300/20 to-transparent'
  },
  afternoon: {
    icon: SunIcon,
    label: 'Afternoon',
    accent: 'from-gold-400/20 to-transparent'
  },
  evening: {
    icon: MoonIcon,
    label: 'Evening',
    accent: 'from-indigo-400/20 to-transparent'
  }
};
const slotVariants = {
  hidden: {
    opacity: 0,
    x: -12
  },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.12,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
export function ItineraryDay({
  dayNumber,
  date,
  activities
}: ItineraryDayProps) {
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
  return <div className="relative flex gap-6" data-id="element-24">
      {/* Timeline connector */}
      <div className="hidden md:flex flex-col items-center pt-1" data-id="element-25">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-400 to-gold-500 flex items-center justify-center text-navy-900 font-serif font-bold text-sm shadow-lg shadow-gold-500/20 flex-shrink-0" data-id="element-26">
          {dayNumber}
        </div>
        <div className="w-px flex-grow bg-gradient-to-b from-gold-400/40 to-transparent mt-3" data-id="element-27" />
      </div>

      {/* Day card */}
      <div className="flex-grow bg-gradient-to-br from-navy-900 via-[#0d1f3c] to-navy-900 rounded border border-gold-500/15 overflow-hidden shadow-xl relative" data-id="element-28">
        {/* Gold left accent */}
        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-gold-400 via-gold-500/50 to-transparent" data-id="element-29" />

        {/* Subtle texture */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(#c9a96e 0.5px, transparent 0.5px)',
        backgroundSize: '20px 20px'
      }} data-id="element-30" />

        <div className="relative z-10 p-6 md:p-8" data-id="element-31">
          {/* Day header */}
          <div className="mb-6 pb-4 border-b border-gold-500/10" data-id="element-32">
            <div className="flex items-center gap-3 md:hidden mb-1" data-id="element-33">
              <span className="w-7 h-7 rounded-full bg-gold-400 flex items-center justify-center text-navy-900 font-serif font-bold text-xs" data-id="element-34">
                {dayNumber}
              </span>
              <h3 className="text-xl font-serif text-white" data-id="element-35">Day {dayNumber}</h3>
            </div>
            <h3 className="text-xl font-serif text-white hidden md:block" data-id="element-36">
              Day {dayNumber}
            </h3>
            <p className="text-gold-400/70 text-sm tracking-wide mt-1" data-id="element-37">
              {formattedDate}
            </p>
          </div>

          {/* Activity slots */}
          <div className="space-y-5" data-id="element-38">
            {activities.map((activity, index) => {
            const config = slotConfig[activity.timeSlot];
            const SlotIcon = config.icon;
            return <motion.div key={activity.timeSlot} custom={index} variants={slotVariants} initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-40px'
            }} className="flex gap-4" data-id="element-39">
                  {/* Time slot icon */}
                  <div className="flex-shrink-0 w-10 h-10 rounded bg-gold-500/10 flex items-center justify-center mt-0.5" data-id="element-40">
                    <SlotIcon className="w-5 h-5 text-gold-400" data-id="element-41" />
                  </div>

                  {/* Content */}
                  <div className="flex-grow min-w-0" data-id="element-42">
                    <div className="flex items-center gap-2 mb-1" data-id="element-43">
                      <span className="text-xs font-medium text-gold-400/60 tracking-widest uppercase" data-id="element-44">
                        {config.label}
                      </span>
                      <span className="text-gold-500/20" data-id="element-45">·</span>
                      <span className="text-xs text-cream-100/40 flex items-center gap-1" data-id="element-46">
                        <ClockIcon className="w-3 h-3" data-id="element-47" />
                        {activity.duration}
                      </span>
                    </div>
                    <h4 className="text-white font-medium text-base mb-1.5" data-id="element-48">
                      {activity.title}
                    </h4>
                    <p className="text-cream-100/50 text-sm leading-relaxed" data-id="element-49">
                      {activity.description}
                    </p>
                    <span className="inline-block mt-2 text-xs text-gold-400/50 bg-gold-500/5 px-2 py-0.5 rounded-full border border-gold-500/10" data-id="element-50">
                      {activity.category}
                    </span>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </div>;
}